import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { KeyRound, RefreshCw, ShieldCheck, Trash2, UserPlus } from 'lucide-react';
import apiClient from '../api/client';
import { useToast } from '../components/ui/Toast';
import { useAuthStore } from '../stores/authStore';
import { Role } from '../auth/roles';
import { Button } from '../components/ui/Button';
import { Input } from '../components/ui/Input';
import { Label } from '../components/ui/Label';
import { useConfirm } from '../components/ui/ConfirmDialog';

interface Collector {
  id: string;
  full_name: string;
  note: string | null;
  created_at: string;
}

interface ListResponse {
  customerId: string;
  enforced: boolean;
  collectors: Collector[];
}

const cid = (): string => crypto.randomUUID();

/**
 * E8 — who may collect parts on this account.
 *
 * Everyone who can hand goods over can READ this list, because checking a driver's name against
 * it is the whole point. Only the management tier can change it: a standing "these people may
 * collect for us" is the customer's instruction, and a desk that could extend the list could
 * authorise the very driver it had just turned away.
 *
 * While the list is empty nothing is refused — the collector's name is simply recorded on the
 * delivery note. The first entry is what switches the check on, which is why adding one says so.
 */
export default function CustomerCollectors({ customerId }: { customerId: string }) {
  const { t } = useTranslation();
  const { showApiError, push } = useToast();
  const [confirm, confirmDialog] = useConfirm();
  const role = useAuthStore((s) => s.user?.role);
  const canEdit = role === Role.OWNER || role === Role.ADMIN || role === Role.MANAGER;

  const [list, setList] = useState<ListResponse | null>(null);
  const [loading, setLoading] = useState(true);
  /* Migration 169 — the account's pickup code: one per B2B customer, on every one of its orders.
     null = a consumer (one code per order, shown on the order itself). */
  const [pickupCode, setPickupCode] = useState<string | null | undefined>(undefined);
  const [rotating, setRotating] = useState(false);
  useEffect(() => {
    apiClient
      .get<{ pickupCode: string | null }>(`/customers/${customerId}/pickup-code`)
      .then(({ data }) => setPickupCode(data.pickupCode))
      .catch(() => setPickupCode(null));
  }, [customerId]);
  const rotate = async (): Promise<void> => {
    const ok = await confirm({
      title: t('collectors.pickupCode.rotateTitle'),
      description: t('collectors.pickupCode.rotateBody'),
      confirmLabel: t('collectors.pickupCode.rotateAction'),
      tone: 'danger',
    });
    if (!ok) return;
    setRotating(true);
    try {
      const { data } = await apiClient.post<{ pickupCode: string; liveOrdersUpdated: number }>(
        `/customers/${customerId}/pickup-code/rotate`,
        {},
        { headers: { 'X-Command-Id': cid() } }
      );
      setPickupCode(data.pickupCode);
      push({
        tone: 'success',
        message: t('collectors.pickupCode.rotated', { count: data.liveOrdersUpdated }),
      });
    } catch (err) {
      showApiError(err);
    } finally {
      setRotating(false);
    }
  };
  const [fullName, setFullName] = useState('');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);

  const load = async (): Promise<void> => {
    setLoading(true);
    try {
      const { data } = await apiClient.get<ListResponse>(`/customers/${customerId}/collectors`);
      setList(data);
    } catch (err) {
      showApiError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, [customerId]); // eslint-disable-line react-hooks/exhaustive-deps

  const add = async (): Promise<void> => {
    if (fullName.trim().length < 2) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post<{ listNowEnforced: boolean }>(
        `/customers/${customerId}/collectors`,
        { fullName: fullName.trim(), note: note.trim() || undefined },
        { headers: { 'X-Command-Id': cid() } }
      );
      push({
        tone: 'success',
        message: data.listNowEnforced ? t('collectors.addedAndEnforced') : t('collectors.added'),
      });
      setFullName('');
      setNote('');
      await load();
    } catch (err) {
      showApiError(err);
    } finally {
      setBusy(false);
    }
  };

  const revoke = async (c: Collector): Promise<void> => {
    const ok = await confirm({
      title: t('collectors.confirmRevoke', { name: c.full_name }),
      description: t('collectors.confirmRevokeBody'),
      confirmLabel: t('collectors.revokeAction'),
      tone: 'danger',
    });
    if (!ok) return;
    try {
      await apiClient.delete(`/customers/${customerId}/collectors/${c.id}`, {
        headers: { 'X-Command-Id': cid() },
      });
      push({ tone: 'success', message: t('collectors.revoked', { name: c.full_name }) });
      await load();
    } catch (err) {
      showApiError(err);
    }
  };

  return (
    <div className="space-y-3">
      {confirmDialog}
      {pickupCode && (
        <div className="flex flex-wrap items-center justify-between gap-3 rounded border px-3 py-2">
          <div className="min-w-0">
            <div className="font-semibold flex items-center gap-2">
              <KeyRound className="h-4 w-4" />
              {t('collectors.pickupCode.title')}
              <span className="font-mono text-base tracking-widest">{pickupCode}</span>
            </div>
            <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
              {t('collectors.pickupCode.body')}
            </p>
          </div>
          {canEdit && (
            <Button size="sm" variant="outline" onClick={() => void rotate()} disabled={rotating}>
              <RefreshCw className="h-4 w-4 mr-1" />
              {t('collectors.pickupCode.rotateAction')}
            </Button>
          )}
        </div>
      )}
      <div className="flex items-start justify-between gap-3">
        <div>
          <h3 className="font-semibold flex items-center gap-2">
            <ShieldCheck className="h-4 w-4" />
            {t('collectors.title')}
          </h3>
          <p className="text-sm text-muted-foreground mt-1 max-w-2xl">
            {list?.enforced ? t('collectors.enforced') : t('collectors.notEnforced')}
          </p>
        </div>
      </div>

      {loading && <p className="text-sm text-muted-foreground">{t('common.loading')}</p>}

      {!loading && list && list.collectors.length === 0 && (
        <p className="text-sm text-muted-foreground">{t('collectors.empty')}</p>
      )}

      {!loading && list && list.collectors.length > 0 && (
        <ul className="divide-y rounded border">
          {list.collectors.map((c) => (
            <li key={c.id} className="flex items-center justify-between gap-3 px-3 py-2">
              <div className="min-w-0">
                <div className="font-medium truncate">{c.full_name}</div>
                {c.note && <div className="text-xs text-muted-foreground truncate">{c.note}</div>}
              </div>
              {canEdit && (
                <Button
                  variant="ghost"
                  onClick={() => void revoke(c)}
                  aria-label={t('collectors.revokeAction')}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              )}
            </li>
          ))}
        </ul>
      )}

      {canEdit ? (
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <Label htmlFor="collector-name">{t('collectors.name')}</Label>
            <Input
              id="collector-name"
              value={fullName}
              onChange={(e) => setFullName(e.target.value)}
              placeholder={t('collectors.namePlaceholder')}
            />
          </div>
          <div>
            <Label htmlFor="collector-note">{t('collectors.note')}</Label>
            <Input
              id="collector-note"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={t('collectors.notePlaceholder')}
            />
          </div>
          <Button onClick={() => void add()} disabled={busy || fullName.trim().length < 2}>
            <UserPlus className="h-4 w-4 mr-2" />
            {t('collectors.add')}
          </Button>
        </div>
      ) : (
        <p className="text-xs text-muted-foreground">{t('collectors.readOnly')}</p>
      )}
    </div>
  );
}
